import { html, LitElement } from 'lit';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

import { getPublicFirestoreUrl } from '../../modules/encoding-helpers.js';
import { q1MemberChipStyles } from './q1-member-chip-styles.js';
import { enrichMember } from '../../modules/member-helpers-client.js';

export class Q1MemberChip extends LitElement {
  static styles = [ q1MemberChipStyles ];

  static properties = {
    
    /**
     * The member object to display.
     * 
     * EITHER SET THIS OR SET `memberRef` and `refPathMap`.
     * 
     * Special Properties:
     * - `chipLink` OR `detailViewPath`: If included these will replace the profile as the member
     *   link. `chipLink` takes precedence over `detailViewPath`.
     */
    member: { type: Object },

    /**
     * The ref path of the member object to display. Requires `refPathMap` as well.
     * 
     * EITHER SET THIS AND `refPathMap` OR SET `member`.
     */
    memberRef: { type: String, attribute: 'member-ref' },

    /**
     * Ref path map used to get the member data from `memberRef`.
     * NOTE: When this is used, a call is made to `enrichMember` to enrich the member data.
     * 
     * EITHER SET THIS AND `memberRef` OR SET `member`.
     */
    refPathMap: { type: Object },
    
    /** Controls whether the external or internal name is displayed and other visibility. */
    isSignedIn: { type: Boolean, attribute: 'signed-in' },

    /** When true, visually indicates a disabled status. */
    disabled: { type: Boolean, reflect: true },

    /** Optionally displays an icon next to the member name. */
    icon: { type: String, reflect: true },

    /** If `icon` is set, use this to specify the library. Defaults to 'material'. */
    iconLibrary: { type: String, attribute: 'icon-library' },
    
    /**
     * If `icon` is set, use this to add a tooltip to the icon.
     * If `icon` is not set then this is ignored.
     */
    iconTooltip: { type: String, attribute: 'icon-tooltip' },

    /**
     * Optionally specify a comma-separated list of extra fields to display after the member name
     * and after the icon (if present). The fields must be present in the member object.
     * 
     * @example
     * <q1-member-chip extra-fields="number,level_label" .member=${member}></q1-member-chip>
     */
    extraFields: { type: String, attribute: 'extra-fields' },

    /**
     * Optionally specify the target for member link. The social links will always open 
     * in a new tab, but by default the member link will open in the same tab.
     */
    target: { type: String, reflect: true },

    /**
     * Defaults to false. If false, the member name will be a link to their profile.
     * If true, the chip is not clickable: The member name will not be a link
     * and `showLinkedin` will be ignored.
     */
    noLinks: { type: Boolean, attribute: 'no-links' },

    /**
     * Defaults to false. If true, a LinkedIn icon will be shown that links to their profile.
     */
    showLinkedin: { type: Boolean, attribute: 'show-linkedin' },
  };

  constructor() {
    super();

    this.member = null;
    this.memberRef = null;
    this.refPathMap = null;
    
    this.isSignedIn = false;
    this.disabled = false;

    this.icon = null;
    this.iconLibrary = 'material';
    this.iconTooltip = null;

    this.extraFields = '';
    this.target = '';
    this.noLinks = false;
    this.showLinkedin = false;
  }

  /*=== LIFECYCLE ===*/

  updated(changedProperties) {
    if (changedProperties.has('memberRef') || changedProperties.has('refPathMap')) {
      if (!this.member) {
        this.member = this.refPathMap?.[this.memberRef];
        enrichMember(this.member, this.refPathMap);
      }
    }
  }

  /*=== GETTERS ===*/

  get isEmpty() {
    return !this.member || !this.member.number;
  }

  get displayName() {
    return this.isSignedIn
      ? this.member?.nameInternal
      : this.member?.nameExternal || this.member?.number;
  }

  get memberLink() {
    if (this.member?.chipLink) return this.member.chipLink;
    if (this.member?.detailViewPath) return this.member.detailViewPath;
    if (this.member?.number) return `/@${this.member?.number}`;
    return '';
  }

  get profileImage() {
    if (
      (typeof this.member?.profile === 'string')
      && this.member.profile.startsWith('http')
    ) return this.member.profile;

    return getPublicFirestoreUrl(this.member?.profile?.mini?.path);
  }

  /*=== TEMPLATES ===*/

  render() {
    return html`
      <item-wrapper part="base" class=${this.disabled ? 'disabled' : ''}>
        ${this.memberNameTemplate}

        ${this.iconTemplate}

        ${this.extraFieldsTemplate}

        ${this.socialLinksTemplate}
      </item-wrapper>
    `;
  }

  get memberNameTemplate() {
    if (this.isEmpty) return html`
      <span class="empty-member">Member info missing</span>
    `;

    if (this.noLinks) {
      return html`
        <span class="member-name" part="name">
          <sl-avatar label="${this.displayName}" image=${this.profileImage}></sl-avatar>
          ${this.displayName}
        </span>
      `;
    }
    
    return html`
      <a href=${this.memberLink} class="member-link" part="name" target="${this.target}">
        <sl-avatar label="${this.displayName}" image=${this.profileImage}></sl-avatar>
        ${this.displayName}
      </a>
    `;
  }

  get iconTemplate() {
    if (this.isEmpty || !this.icon) return '';

    if (!this.iconTooltip) return html`
      <sl-icon part="icon" library="${this.iconLibrary}" name="${this.icon}"></sl-icon>
    `;

    return html`
      <sl-tooltip content="${this.iconTooltip}">
        <sl-icon part="icon" library="${this.iconLibrary}" name="${this.icon}"></sl-icon>
      </sl-tooltip>
    `;
  }

  get extraFieldsTemplate() {
    if (this.isEmpty || !this.extraFields?.trim().length) return '';

    const extraFieldValues = this.extraFields.split(',')
      .map(field => field.trim())
      .filter(field => field && this.member[field])
      .map(field => this.member[field]);

    return extraFieldValues.map(fieldValue => html`
      <span class="extra-field" part="extra-field">${fieldValue}</span>
    `);
  }

  get socialLinksTemplate() {
    if (this.noLinks || !this.showLinkedin || !this.member?.linkedinProfileUrl) return '';

    return html`
      <item-spacer></item-spacer>

      <sl-tooltip content="Linkedin Profile" part="social-link">
        <sl-icon-button
          class="linkedin-link"
          library="tabler"
          name="brand-linkedin"
          href="${this.member?.linkedinProfileUrl}"
          target="_blank"
        ></sl-icon-button>
      </sl-tooltip>
    `;
  }

}

customElements.define('q1-member-chip', Q1MemberChip);
